import React from "react";
import {
  Panel,
  Heading,
  formatCurrency,
  theme,
  Icon,
  Input,
  Text,
  useResponsive,
  FlexGrid,
  CustomImage,
} from "@clearabee/ui-library-native";
import {
  ApiResponseData,
  FlatCatalogue,
  instance,
  isQuantitative,
} from "@clearabee/ui-sdk";
import placeholderImage from "lib/images/placeholder.png";
import { Image } from "../../components/Image/Image";
import { SkeletonLoader } from "components/SkeletonLoader/SkeletonLoader";
import { View, TouchableOpacity, Platform } from "react-native";
import { Rect } from "react-native-svg";
import { styles, ids } from "./ProductDetailsPanel.styles";
import { useTranslation } from "react-i18next";
import { useFormContext } from "lib/hooks/useFormContext";
import { SKIP_WASTE_TYPES } from "lib/constants/skipWasteTypes";
import { useCalloutChargeModal } from "lib/hooks/useCalloutChargeModal";
import { ProductDetailsUpload } from "components/ProductDetailsUpload/ProductDetailsUpload";

interface ProductDetailsPanelProps {
  product: FlatCatalogue["items"][0];
  hasNoCalloutCharge: boolean;
  calloutCharge?: ApiResponseData<typeof instance.catalogues.getCalloutCharge>;
  quantity: number;
  onImageChange: (files: File[]) => void;
  categorySku: string;
  wasteType?: string;
  exchangeOrderRef?: string;
  initialImages?: CustomImage[];
}

export const ProductDetailsPanel = ({
  product,
  hasNoCalloutCharge,
  calloutCharge,
  quantity,
  onImageChange,
  categorySku,
  wasteType,
  exchangeOrderRef,
  initialImages,
}: ProductDetailsPanelProps): React.ReactElement => {
  const [translate] = useTranslation();
  const { screens } = useResponsive();
  const [CalloutChargeModal, showCalloutChargeModal] = useCalloutChargeModal();
  const wasteTypeLabel = SKIP_WASTE_TYPES.find(
    ({ value }) => value === wasteType,
  )?.label;
  const { Field } = useFormContext();

  const productImage = (
    <View style={styles.imageContainer} dataSet={{ media: ids.imageContainer }}>
      <Image
        style={styles.image}
        source={product.img ?? placeholderImage}
        resizeMode="contain"
        objectPosition={(screens.small && "center 90%") || undefined}
      />
    </View>
  );

  return (
    <>
      <Panel
        styles={styles.panel}
        dataSet={{ media: ids.panel }}
        shadow={Platform.OS === "web"}
      >
        <Heading
          level={5}
          color="brand"
          styles={styles.productTitle}
          dataSet={{ media: ids.productTitle }}
        >
          {product.title}
        </Heading>
        {exchangeOrderRef && (
          <Text
            styles={styles.productDescription}
            dataSet={{ media: ids.productDescription }}
          >
            {translate("common.exchangeOrderRef")} - {exchangeOrderRef}
          </Text>
        )}
        {wasteTypeLabel && (
          <Text
            styles={styles.productDescription}
            dataSet={{ media: ids.productDescription }}
          >
            {translate("common.wasteType")} - {translate(wasteTypeLabel)}
          </Text>
        )}
        {!exchangeOrderRef && (
          <Text
            styles={styles.productDescription}
            dataSet={{ media: ids.productDescription }}
          >
            {product.description}
          </Text>
        )}
        <FlexGrid style={{ flex: 1 }} cellSpacing={null}>
          <FlexGrid.Cell base="100%" small="50%">
            <View
              style={styles.productDetails}
              dataSet={{ media: ids.productDetails }}
            >
              {!screens.small && Platform.OS === "web" && productImage}

              <View
                style={styles.priceContainer}
                dataSet={{ media: ids.priceContainer }}
              >
                <View
                  style={[
                    !hasNoCalloutCharge && styles.priceInnerContainer,
                    hasNoCalloutCharge &&
                      screens.medium && {
                        alignItems: "flex-start",
                      },
                  ]}
                >
                  <Heading level={3}>
                    {product.price && formatCurrency(product.price * quantity)}
                  </Heading>
                  <Text fontSize="small">
                    {!hasNoCalloutCharge && " "}
                    {translate(
                      hasNoCalloutCharge
                        ? "common.includingVat"
                        : "common.incVat",
                    )}
                  </Text>
                </View>

                {!hasNoCalloutCharge && (
                  <View style={{ marginTop: theme.spacing.xsmall2 }}>
                    {!calloutCharge && (
                      <SkeletonLoader
                        speed={1}
                        backgroundColor={theme.colors.warning.lighter}
                        foregroundColor={theme.colors.warning.base}
                        height={28}
                        width={215}
                      >
                        <Rect
                          x="0"
                          y="0"
                          rx="8"
                          ry="8"
                          width="100%"
                          height="100%"
                        />
                      </SkeletonLoader>
                    )}
                    {calloutCharge?.price && (
                      <TouchableOpacity
                        onPress={() => showCalloutChargeModal(true)}
                      >
                        <Panel
                          styles={styles.calloutChargePanel}
                          shadow={false}
                          color="warning"
                        >
                          <Text
                            fontSize="small"
                            styles={{
                              marginRight: theme.spacing.xsmall2,
                            }}
                          >
                            +
                            {translate(
                              "hwa.screens.productDetails.calloutCharge",
                              {
                                calloutCharge: formatCurrency(
                                  calloutCharge.price,
                                ),
                              },
                            )}
                          </Text>

                          <Icon.Info size="small" color="dark" />
                        </Panel>
                      </TouchableOpacity>
                    )}
                  </View>
                )}
              </View>

              {isQuantitative(product) && (
                <View>
                  <Field
                    name="quantity"
                    styles={{
                      marginBottom: 0,
                      marginTop: theme.spacing.small,
                    }}
                  >
                    {({ field }) => (
                      <Input.Quantity
                        variant="small"
                        {...field}
                        min={1}
                        max={14}
                      />
                    )}
                  </Field>
                </View>
              )}
              {(categorySku !== "BINCOL" || !!initialImages?.length) && (
                <View
                  style={styles.imagePanelWrapper}
                  dataSet={{ media: ids.imagePanelWrapper }}
                >
                  <ProductDetailsUpload
                    onImageChange={onImageChange}
                    initialImages={initialImages}
                  />
                </View>
              )}
            </View>
            {categorySku !== "BINCOL" && (
              <Text
                styles={styles.photoExplanation}
                dataSet={{ media: ids.photoExplanation }}
              >
                {categorySku !== "SKIPS"
                  ? translate("hwa.screens.productDetails.photoExplanation")
                  : translate(
                      "hwa.screens.productDetails.skipPhotoExplanation",
                    )}
              </Text>
            )}
          </FlexGrid.Cell>
          {screens.small && (
            <FlexGrid.Cell base="50%">{productImage}</FlexGrid.Cell>
          )}
        </FlexGrid>
      </Panel>
      <CalloutChargeModal price={calloutCharge?.price ?? 0} />
    </>
  );
};
